import React from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { EventParamList } from '../../typings/Params';
import Filters from '../../components/Filter';
import { useState } from 'react';
import TypeDropdown from './TypeDropdown';
import PriorityDropdown from './PriorityDropdown';
import OccurrenceTypeDropdown from '../../components/dispatch/OccurrenceTypeDropdown';
import TagsDropdown from './TagsDropdown';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import FilterDatePicker from '../../components/Filter/components/FilterDateInput';
import { translate } from '../../services/translate';
import moment from 'moment';
import FilterCheckBox from '../../components/Filter/components/FilterCheckBox';
import { View } from 'react-native';
import MyAppText from '../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { OccurrencePriorities } from '../../schemas/dispatch';
import PolygonTypeDropdown from '../../components/PolygonTypeDropdown';
import PolygonsByTypeDropdown from '../../components/PolygonsByTypeDropdown';

interface Props {
    filters: EventParamList['EventsBoard'];
    setFilters: React.Dispatch<React.SetStateAction<EventParamList['EventsBoard']>>;
    authenticatedUser?: AuthenticatedUser;
}

function EventsFilter({ filters, setFilters, authenticatedUser }: Props) {
    const navigation = useNavigation<NavigationProp<EventParamList, 'EventsBoard'>>();
    const { styles } = useStyles(styleSheet);

    const [selectedTriggerTypeFilters, setSelectedTriggerTypeFilters] = useState<(TriggerType | 'manual')[]>(filters.triggerType ?? []);
    const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<OccurrencePriorities | 'all'>(filters.priority ?? 'all');
    const [selectedOccurrenceTypeFilter, setSelectedOccurrenceTypeFilter] = useState<number>(filters.occurrenceTypeId ?? 0);
    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>(filters.tags ?? []);
    const [selectedMapPolygonsIds, setSelectedMapPolygonsIds] = useState<number[]>(filters.mapPolygonIds ?? []);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType || '');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [withDispatch, setWithDispatch] = useState<boolean>(filters.withDispatch ?? false);
    const [withoutDispatch, setWithoutDispatch] = useState<boolean>(filters.withoutDispatch ?? false);

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const handleCleanFilter = () => {
        setSelectedOccurrenceTypeFilter(0);
        setSelectedActingBodyId(0);
        setSelectedPriorityFilter('all');
        setSelectedTriggerTypeFilters([]);
        setSelectedTagsIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setWithDispatch(false);
        setWithoutDispatch(false);
        setSelectedMapPolygonsIds([]);
        setSelectedPolygonType('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerType: selectedTriggerTypeFilters.length ? selectedTriggerTypeFilters : undefined,
            occurrenceTypeId: selectedOccurrenceTypeFilter !== 0 ? selectedOccurrenceTypeFilter : undefined,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
        });

        navigation.setParams({
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerType: selectedTriggerTypeFilters.length ? selectedTriggerTypeFilters : undefined,
            occurrenceTypeId: selectedOccurrenceTypeFilter !== 0 ? selectedOccurrenceTypeFilter : undefined,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
        });

    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrences) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={!!selectedPolygonType && !selectedMapPolygonsIds.length}
        >
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences) &&
                <ActingBodiesDropdown value={selectedActingBodyId} setValue={setSelectedActingBodyId} zIndex={10} />
            }
            <PriorityDropdown value={selectedPriorityFilter} setValue={setSelectedPriorityFilter} zIndex={9} />
            <TypeDropdown value={selectedTriggerTypeFilters} setValue={setSelectedTriggerTypeFilters} zIndex={8} />
            <OccurrenceTypeDropdown
                value={selectedOccurrenceTypeFilter}
                setValue={setSelectedOccurrenceTypeFilter}
                zIndex={7}
                actingBodyId={getActingBodyId()}
            />
            <TagsDropdown value={selectedTagsIds} setValue={setSelectedTagsIds} zIndex={5} />
            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('beginIn')}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(6)}
                />
            </View>

            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(5)}
                />
            </View>
            <PolygonTypeDropdown
                label={translate('regions')}
                dropDownDirection='BOTTOM'
                zIndex={4}
                setPolygonType={setSelectedPolygonType}
                polygonType={selectedPolygonType}
                setMapPolygonsIds={setSelectedMapPolygonsIds}
            />
            <PolygonsByTypeDropdown
                label={translate('subRegions')}
                dropDownDirection='BOTTOM'
                values={selectedMapPolygonsIds}
                setValues={setSelectedMapPolygonsIds}
                polygonType={selectedPolygonType}
                showTooltip={!selectedPolygonType}
                tooltipText={translate('selectRegionTooltip')}
                zIndex={3}
                setPendingLoading={setPolygonPendingLoading}
                disabled={!selectedPolygonType}
                required={!!selectedPolygonType}
            />
            <View>
                <MyAppText style={styles.filterLabel}>{translate('withDispatch')}</MyAppText>
                <View style={{ flexDirection: 'row' }}>
                    <FilterCheckBox
                        label={translate('yes')}
                        value={withDispatch}
                        onChange={(value) => {
                            setWithDispatch(value);
                            if (value) {
                                setWithoutDispatch(!value);
                            }
                        }}
                    />
                    <FilterCheckBox
                        label={translate('no')}
                        value={withoutDispatch}
                        onChange={(value) => {
                            setWithoutDispatch(value);
                            if (value) {
                                setWithDispatch(!value);
                            }
                        }}
                    />
                </View>
            </View>
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
}));

export default EventsFilter;
